import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useContent } from '../context/ContentContext';

const FooterWrapper = styled.footer`
  background: #1a1a1a;
  color: #999;
  padding: 4rem 2rem 2rem;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 3rem;
  margin-bottom: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FooterCol = styled.div`
  h3 {
    color: white;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
    font-weight: 600;
  }

  p {
    line-height: 1.8;
    font-size: 0.95rem;
    white-space: pre-line;
  }

  ul {
    list-style: none;
    padding: 0;
  }

  li {
    margin-bottom: 0.8rem;
  }

  ul a {
    color: #999;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: #ff5722;
    }
  }
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: white;
  margin-bottom: 1rem;
`;

const LogoBox = styled.div`
  background: white;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid var(--primary-color);
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;
`;

const LogoMain = styled.span`
  font-weight: 700;
  font-size: 1.2rem;
  letter-spacing: 1px;
`;

const LogoSub = styled.span`
  font-size: 0.8rem;
  font-weight: 400;
`;

const FooterBottom = styled.div`
  border-top: 1px solid #333;
  padding-top: 2rem;
  text-align: center;
  font-size: 0.9rem;
`;

const FooterCredit = styled.p`
  margin: 0.5rem 0 0;
  color: #bdbdbd;
  font-size: 0.85rem;

  a {
    color: #ff7a45;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const FooterCTA = styled(Link)`
  min-width: 210px;
  height: 56px;
  padding: 0 1.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff7a45 0%, #ff5f1f 100%);
  color: #fff7f0;
  text-decoration: none;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  box-shadow: 0 14px 28px rgba(255, 95, 31, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    filter: brightness(1.04);
    box-shadow: 0 18px 34px rgba(255, 95, 31, 0.34);
    color: #ffffff;
  }

  &:focus-visible {
    outline: 3px solid rgba(255, 122, 69, 0.45);
    outline-offset: 3px;
  }
`;

const Footer = () => {
  const siteData = useContent();
  return (
    <FooterWrapper>
      <Container>
        <FooterGrid>
          <FooterCol>
            <FooterLogo>
              <LogoBox>
                <LogoImage src="/hasnat.png" alt={`${siteData.company.name} logo`} />
              </LogoBox>
              <LogoText>
                <LogoMain>{siteData.company.name}</LogoMain>
                <LogoSub>{siteData.company.tagline}</LogoSub>
              </LogoText>
            </FooterLogo>
            <p style={{ marginTop: '1rem' }}>
              {siteData.company.description}
            </p>
          </FooterCol>

          <FooterCol>
            <h3>Head Office</h3>
            <p>
              {siteData.contact?.contactInfo?.[2]?.content || '123 Packaging Street\nIndustrial Zone, City, State 12345'}
            </p>
          </FooterCol>

          <FooterCol>
            <h3>Quick Links</h3>
            <ul>
              {siteData.navigation.map((item, index) => (
                <li key={index}>
                  <Link to={item.path}>{item.name}</Link>
                </li>
              ))}
            </ul>
          </FooterCol>

          <FooterCol>
            <h3>Inquiries</h3>
            <p>
              For any inquiries, questions<br />
              or commendations, please<br />
              call: {siteData.contact?.contactInfo?.[0]?.content || '123-456-7890'}
            </p>
            <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center' }}>
              <FooterCTA to="/contact">Contact Us</FooterCTA>
            </div>
          </FooterCol>
        </FooterGrid>

        <FooterBottom>
          <p>&copy; {new Date().getFullYear()} {siteData.company.fullName}. All rights reserved.</p>
          <FooterCredit>
            Developed and Managed by{' '}
            <a href="https://www.cyrtechnologies.com" target="_blank" rel="noreferrer">
              CYR Technologies
            </a>
          </FooterCredit>
        </FooterBottom>
      </Container>
    </FooterWrapper>
  );
};

export default Footer;
